import { settingsWords as words } from "./settings-copy";
import { useRef, useState } from "react";
import type { TabDraft } from "@/components/ui/box-draft";
import type { UseQueryResult } from "@tanstack/react-query";
import type { Read, Result, BoxState } from "@/components/ui/box.schema";
import { ApiError } from "@/lib/api/client";
import { checkCommit, reasonOnError } from "@/lib/api/commitSeams";
import { settingsCopy as copy } from "./settings-copy";

export const imam = { system: "imam" } as const;

/** React Query's last successful value survives a failed refresh (R2). */
export function boxRead<T>(
  query: UseQueryResult<T>,
  sentence = copy.unavailable,
): Read<T> {
  if (query.data !== undefined) {
    const readAt = new Date(query.dataUpdatedAt).toISOString();
    if (query.isError)
      return {
        status: "stale",
        last: query.data,
        readAt,
        sentence: copy.stale,
        refresh: () => void query.refetch(),
      };
    return { status: "ready", value: query.data, readAt };
  }
  if (query.isError)
    return { status: "failed", sentence, retry: () => void query.refetch() };
  return { status: "loading" };
}

/** The command that stays beside a browser draft until Check answers it. */
export type PendingSettingsSave = { key: string; effects: string[] };
type KeptCommit = {
  keep: Pick<TabDraft<unknown, PendingSettingsSave>, "pending" | "setPending">;
  refresh: () => Promise<unknown>;
  versionField: "settings_version" | "grade_scale_version" | "version";
  onChecked?: () => void;
};

/** Unknown never repeats the command, including after this tab reloads. */
export function useSettingsCommit(kept?: KeptCommit) {
  const inFlight = useRef(false);
  const [pending, setPending] = useState(false);
  const [settledResult, setResult] = useState<Result>({ state: "none" });
  const confirm = (effects: string[], version?: unknown): Result => ({
    state: "confirmed", at: new Date().toISOString(), effects, remains: [],
    ...(typeof version === "string" || typeof version === "number" ? { version: String(version) } : {}),
  });
  const unknown = (key: string, effects: string[], refresh: () => Promise<unknown>): Result => ({
    state: "unknown", cap: "", requestId: key, sentence: copy.unknown,
    check: {
      verb: words.check, note: words.readTheResultOfThisSave,
      run: async () => {
        let next: Result;
        try {
          const checked = await checkCommit(key);
          if (checked.state === "confirmed") {
            await refresh();
            kept?.onChecked?.();
            // A later read may already include another writer. Only this receipt
            // establishes the version made by this command (R4).
            next = confirm(effects, kept ? checked.receipt.result[kept.versionField] : undefined);
          } else if (checked.state === "not_committed") {
            next = { state: "refused", cap: words.notSaved,
              sentence: words.thisSaveDidNotGoThroughReviewTheDraftAndSaveItWhenReady, draft: "kept" };
          } else next = unknown(key, effects, refresh);
        } catch { next = unknown(key, effects, refresh); }
        if (next.state !== "unknown") kept?.keep.setPending(null);
        setResult(next);
        return next;
      },
    },
  });
  const restored = kept?.keep.pending;
  const result = settledResult.state === "none" && restored && !inFlight.current
    ? unknown(restored.key, restored.effects, kept.refresh) : settledResult;
  const reset = () => {
    if (result.state !== "unknown" && result.state !== "partial") setResult({ state: "none" });
  };
  const run = async (
    key: string, write: (key: string) => Promise<unknown>, effects: string[], refresh: () => Promise<unknown>,
  ): Promise<Result> => {
    // Mark the live send before publishing its key through the external tab
    // store. That synchronous render must keep Sending, rather than restore Check.
    inFlight.current = true;
    setPending(true);
    kept?.keep.setPending({ key, effects });
    let next: Result;
    try {
      const version = await write(key);
      next = confirm(effects, version);
    } catch (error) {
      next = error instanceof ApiError && error.status >= 400 && error.status < 500
        ? { state: "refused", cap: words.notSaved, sentence: reasonOnError(error)?.message ?? error.message, draft: "kept" }
        : unknown(key, effects, refresh);
    }
    if (next.state !== "unknown") kept?.keep.setPending(null);
    inFlight.current = false;
    setPending(false);
    setResult(next);
    return next;
  };
  return { result, pending, reset, run };
}

export function resultState(result: Result, settled: BoxState): BoxState {
  if (result.state === "unknown")
    return { class: "reading", word: words.notConfirmed };
  if (result.state === "refused")
    return { class: "failed", word: words.notSaved };
  if (result.state === "partial")
    return { class: "failed", word: words.partlyDone };
  return settled;
}
